import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { sendContactForm } from "@/utils/emailService";

// Modified schema to make website URL validation less strict
const formSchema = z.object({
  name: z.string().min(2, { message: "Name must be at least 2 characters" }),
  email: z.string().email({ message: "Please enter a valid email address" }),
  phone: z.string().optional(),
  website: z.string().optional(), // No URL validation here, allowing simple entries
});

type FormValues = z.infer<typeof formSchema>;

const LeadCaptureModal = () => {
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      website: "",
    },
  });

  useEffect(() => {
    // Get current page visit count from localStorage
    const visitCount = parseInt(localStorage.getItem("pageVisitCount") || "0");
    const hasSeenModal = localStorage.getItem("hasSeenLeadModal") === "true";
    
    if (hasSeenModal) return;
    
    // Track if user has been on the site for 30 seconds
    const thirtySecondTimer = setTimeout(() => {
      if (!hasSeenModal) {
        setOpen(true);
        localStorage.setItem("hasSeenLeadModal", "true");
      }
    }, 30000);
    
    // If user has visited 2 or more pages, show modal after 10 seconds
    if (visitCount >= 2) {
      const tenSecondTimer = setTimeout(() => {
        if (!hasSeenModal) {
          setOpen(true);
          localStorage.setItem("hasSeenLeadModal", "true");
        }
      }, 10000);
      
      return () => {
        clearTimeout(thirtySecondTimer);
        clearTimeout(tenSecondTimer);
      };
    }
    
    return () => clearTimeout(thirtySecondTimer);
  }, []);

  const onSubmit = async (data: FormValues) => {
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    try {
      // Format lead data for the contact form service
      const leadData = {
        name: data.name,
        email: data.email,
        phone: data.phone || "",
        website: data.website || "",
        message: "Submitted via lead capture modal",
        service: "General Inquiry"
      };
      
      // Use the shared email service to save the lead to Supabase
      const success = await sendContactForm(leadData);
      
      if (!success) throw new Error("Failed to submit lead");
      
      toast({
        title: "Thanks for reaching out!",
        description: "We'll get back to you as soon as possible.",
        duration: 5000,
      });
      
      // Close the modal
      setOpen(false);
      
      // Set session storage to indicate form was submitted
      sessionStorage.setItem("formSubmitted", "true");
      
      // Navigate to thank you page
      navigate("/thank-you");
    } catch (error) {
      console.error("Error submitting lead:", error);
      toast({
        title: "Something went wrong",
        description: "Please try again or contact us directly.",
        variant: "destructive",
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">Get Your Free SEO Consultation</DialogTitle>
          <DialogDescription>
            <span className="text-desert-sunset font-medium">100% Free, No Obligation</span> - Discover how we can help improve your website's rankings and traffic. Enter your details below to get started.
          </DialogDescription>
        </DialogHeader>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Your name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="your.email@example.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Phone Number (Optional)</FormLabel>
                  <FormControl>
                    <Input placeholder="(123) 456-7890" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="website"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Website (Optional)</FormLabel>
                  <FormControl>
                    <Input placeholder="yourwebsite.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <div className="flex justify-end space-x-4 pt-4">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={isSubmitting}>
                Maybe Later
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Submitting...' : 'Get My Free Consultation'}
              </Button>
            </div>
          </form>
        </Form>
        
        <div className="pt-2 mt-2 border-t text-xs text-muted-foreground">
          <p className="flex items-center">
            <span className="mr-1">✓</span> No credit card required
          </p>
          <p className="flex items-center">
            <span className="mr-1">✓</span> No obligation to purchase
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LeadCaptureModal;
